class StringUtils {
  /**
   * Returns the string passed as argument, or the empty string "" if argument is null or undefined
   * @param string String to return, if not null nor undefined
   * @returns {*} String passed as argument if not null nor undefined, otherwise the empty string ""
   */
  static nullToEmpty = (string) => {
    return string == null ? "" : string;
  };

  /**
   * Returns the number passed as argument, or 0 if argument is null or undefined
   * @param number Number to return, if not null nor undefined
   * @returns {*} Number passed as argument if not null nor undefined, otherwise 0
   */
  static nullToZero = (number) => {
    return number == null ? 0 : number;
  };

  /**
   * Returns the number passed as argument, or 0 if argument is null or undefined.
   * When number has more than the specified number of digits, it is formatted as
   * +99 (for max 2 digits).
   * @param number Number to return, if not null nor undefined
   * @param digits Max number of digits
   * @returns {*} Number passed as argument if not null nor undefined, otherwise 0
   */
  static nullToZeroWithMaxDigits = (number, digits) => {
    const nullToZero = StringUtils.nullToZero(number);

    if (digits > 0 && nullToZero >= Math.pow(10, digits)) {
      return "+" + "9".repeat(digits);
    } else return nullToZero;
  };

  /**
   * Returns true if the string is either null, undefined or empty ("")
   * @param string String
   * @returns {boolean} True if string is null, undefined or empty ("")
   */
  static isNullOrEmpty = (string) => {
    return StringUtils.nullToEmpty(string) === "";
  };

  /**
   * Returns true if the string is either "1", "true" or "TRUE", otherwise returns false.
   * @param string String to analyze (such as a configuration parameter from file)
   * @return {boolean} True if string is either "1", "true" or "TRUE"
   */
  static stringIsTrue = (string) => {
    return string === "1" || string === "true" || string === "TRUE";
  };

  /**
   * Normalize strings so that close duplicates can be removed.
   * Trim, then convert to lowercase.
   * @param string
   * @returns {string}
   */
  static normalizeString = (string) => {
    // For now, do not remove accents, because fields indexed in GraphQL are case-insensitive (collation strength = 2)
    // but they are still diacritics-sensitive. So it's ok to display to user the accented variation and unaccented
    // variation of a string because they will produce 2 different search results.
    // More info on using .normalize to remove accents:
    // https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
    // return string.trim().toLocaleLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    return string.trim().toLocaleLowerCase();
  };

  /**
   * Split a string on spaces
   * @param string String to tokenize
   * @return {*} Tokens
   */
  static tokenize = (string) => {
    return StringUtils.nullToEmpty(string)
      .split(" ")
      .filter((x) => x.length > 0);
  };

  /**
   * Escapes all special characters in a string so that it can be used in a RegExp.
   * @param string String to escape
   * @return {*} Escaped string
   */
  static escapeForRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
  }

  /**
   * Returns whether a string is a valid email.
   * @param string Email
   * @returns {boolean} True if string is a valid email
   */
  static isEmail(string) {
    return StringUtils.emailRegexp().test(string);
  }

  /**
   * Regular expression that defines a valid email string
   * @returns {RegExp} Email pattern
   */
  static emailRegexp() {
    return /[^@]+@[^@]+\.[^@]+/;
  }

  /**
   *Éviter de soumettre des champs avec seulement des espaces
   * @returns {RegExp}
   */
  static requiredRegexp() {
    return /[^\s]/;
  }

  /**
   * Cette fonction enleve le / a la fin de l'url s'il y en a un
   * @param {*} str url
   * @returns l'url sans le / a la fin
   */
  static stripTrailingSlash(str) {
    if (this.nullToEmpty(str).substr(-1) === "/") {
      return str.substr(0, str.length - 1);
    }
    return str;
  }

  /**
   * Capitalizes first letter of every token of a string (ex. new york -> New York)
   * @param string
   */
  static capitalizeFirstWords = (string) => {
    let normalizedChars = [];
    for (let i = 0, beginWord = true; i < string.length; i++) {
      const currentChar = string.charAt(i);
      let normalizedChar = currentChar;

      // If we're at the beginning of a word, make the letter uppercase
      if (beginWord) {
        normalizedChar = normalizedChar.toLocaleUpperCase();
      }

      // If we're looking at a token separator, mark beginWord as true, otherwise it's false
      beginWord = currentChar === " " || currentChar === "-";

      normalizedChars.push(normalizedChar);
    }

    return normalizedChars.join("");
  };
}

export default StringUtils;
